import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import styles from './CommandPalette.module.css';

const CommandPalette = () => {
  const { currentDesign, cycleDesign, isDarkMode, toggleTheme } = useTheme();
  const { data, t } = useLanguage();
  const personalData = data.personal;
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);

  const commands = useMemo(() => {
    const sections = [
      { id: 'home', label: t('command.home'), hint: t('command.homeHint'), icon: 'fas fa-home' },
      { id: 'projects', label: t('command.projects'), hint: t('command.projectsHint'), icon: 'fas fa-folder-open' },
      { id: 'skills', label: t('command.skills'), hint: t('command.skillsHint'), icon: 'fas fa-code' },
      { id: 'experience', label: t('command.experience'), hint: t('command.experienceHint'), icon: 'fas fa-briefcase' },
      { id: 'contact', label: t('command.contact'), hint: t('command.contactHint'), icon: 'fas fa-paper-plane' }
    ];
    const social = personalData.socialLinks
      .map(link => ({ id: link.icon, label: link.name, hint: t('command.external'), icon: `fab ${link.icon}`, url: link.url }));
    return [
      ...sections,
      { id: 'design', label: t('command.nextDesign'), hint: `${t('command.currently')}: ${t(`designNames.${currentDesign.id}`)}`, icon: 'fas fa-layer-group', action: cycleDesign },
      { id: 'mode', label: t('command.switchMode'), hint: t('command.sharedMode'), icon: isDarkMode ? 'fas fa-sun' : 'fas fa-moon', action: toggleTheme },
      { id: 'email', label: t('command.email'), hint: personalData.email, icon: 'fas fa-envelope', url: `mailto:${personalData.email}` },
      ...social
    ];
  }, [currentDesign, cycleDesign, isDarkMode, toggleTheme, personalData, t]);

  const filteredCommands = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return commands;
    return commands.filter(command =>
      `${command.label} ${command.hint}`.toLowerCase().includes(normalizedQuery)
    );
  }, [commands, query]);

  const closePalette = () => {
    setIsOpen(false);
    setQuery('');
    setActiveIndex(0);
  };

  const runCommand = command => {
    if (!command) return;

    if (command.action) {
      command.action();
    } else if (command.url) {
      if (command.url.startsWith('mailto:')) {
        window.location.href = command.url;
      } else {
        window.open(command.url, '_blank', 'noopener,noreferrer');
      }
    } else if (command.id === 'home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      document.getElementById(command.id)?.scrollIntoView({ behavior: 'smooth' });
    }

    closePalette();
  };

  useEffect(() => {
    const handleGlobalKeyDown = event => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setIsOpen(open => !open);
      }
      if (event.key === 'Escape') closePalette();
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setActiveIndex(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isOpen]);

  useEffect(() => {
    if (activeIndex >= filteredCommands.length) setActiveIndex(0);
  }, [activeIndex, filteredCommands.length]);

  const handleInputKeyDown = event => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex(index => (index + 1) % Math.max(filteredCommands.length, 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(index => (index - 1 + filteredCommands.length) % Math.max(filteredCommands.length, 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      runCommand(filteredCommands[activeIndex]);
    }
  };

  return (
    <>
      <button
        className={styles.trigger}
        onClick={() => setIsOpen(true)}
        aria-label={t('command.quickNav')}
        aria-haspopup="dialog"
      >
        <i className="fas fa-terminal" aria-hidden="true"></i>
        <span>{t('command.quickNav')}</span>
        <kbd>Ctrl K</kbd>
      </button>

      {isOpen && createPortal(
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/no-noninteractive-element-interactions
        <div className={styles.backdrop} onMouseDown={closePalette}>
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div
            className={styles.palette}
            role="dialog"
            aria-modal="true"
            aria-label={t('command.quickNav')}
            tabIndex="-1"
            onMouseDown={event => event.stopPropagation()}
          >
            <div className={styles.searchBox}>
              <i className="fas fa-search" aria-hidden="true"></i>
              <input
                ref={inputRef}
                value={query}
                onChange={event => {
                  setQuery(event.target.value);
                  setActiveIndex(0);
                }}
                onKeyDown={handleInputKeyDown}
                placeholder={t('command.placeholder')}
                aria-label={t('command.search')}
              />
              <kbd>ESC</kbd>
            </div>

            <div className={styles.commandList} role="listbox">
              {filteredCommands.length > 0 ? filteredCommands.map((command, index) => (
                <button
                  key={command.id}
                  className={`${styles.command} ${index === activeIndex ? styles.active : ''}`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => runCommand(command)}
                  role="option"
                  aria-selected={index === activeIndex}
                >
                  <span className={styles.commandIcon}>
                    <i className={command.icon} aria-hidden="true"></i>
                  </span>
                  <span className={styles.commandText}>
                    <strong>{command.label}</strong>
                    <small>{command.hint}</small>
                  </span>
                  <i className="fas fa-arrow-right" aria-hidden="true"></i>
                </button>
              )) : (
                <div className={styles.emptyState}>
                  <i className="fas fa-search" aria-hidden="true"></i>
                  <p>{t('command.noMatch')}</p>
                  <span>{t('command.noMatchHint')}</span>
                </div>
              )}
            </div>

            <div className={styles.footer}>
              <span><kbd>↑</kbd><kbd>↓</kbd> {t('command.navigate')}</span>
              <span><kbd>↵</kbd> {t('command.select')}</span>
              <span>{t('command.tagline')}</span>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default CommandPalette;
